import React, {useState, useMemo, useRef, useEffect, useCallback} from 'react';
import { usePersistedDataStore, useCompStore, useLocalDataStore } from '../store';
import MuiTable from "./MuiTable";
import { toast } from '../../common/toast';
import PropertyMap from './Map/Map';
import { pickMapInventory, overlayMapInventory } from './Map/tooltipHelpers';
import IconButton from '@mui/material/IconButton';
import axiosInstance, { authorizedFetch } from '../../fetchConfig/axiosConfig';
import MiscAdjustmentModal from './MiscAdjustmentModal';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { useStreamProcessorWorker } from '../../hooks/useStreamProcessorWorker';
import {calculateHeaders} from '../../common/CalculateHeaderFlex';
import { useUser } from '../UserContext';
import { getBaseURL } from '../../utils/config';
import { specialFormatters, AVTN_BACKEND_UPDATE_KEYS } from '../../common/mappings';
import { generateErrorFlags, generateErrorMessages, generateErrorCodes } from '../../utils/compErrorFlagging';
import { calculateDistance } from '../../common/dateUtils';
import { createColumnDefinitions, COLUMN_ORDER } from './config/columnDefinitions';
import { useColumnLayout, getDefaultColumnVisibility, SALE_ONLY_COLUMNS } from './config/columnVisibility';
import { createRows as createRowsService, createSubjectRow, processRowData } from './services/rowConstructionService';
import { useRarHistory } from './utils/calculatedTav';
import UpdatePollingComponent from './UpdatePollingComponent';
import { isCaseSettled } from './SummaryCaseTable/utils/helpers';
import { createSaveObject } from './SaveComps/createSaveObject';
import { batchSaveComps, resolveCaseNotesForCase } from './SaveComps/batchSaveComps';
import { findCompIndex, mapCompIds, normalizeCompId, selectionSetHasRow } from './common/compSheetLookup';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatDateWithoutTimezone } from '../../common/dateUtils';
import { TimeAdjContextProvider } from './TimeAdjustments/TimeAdjContext';
import { fetchKNNModel } from '../../services/fetchKNNModel';
import { caseRefreshAssignmentLog, replacedSheets, sheetForParcel } from './CaseReviewHelpers/refreshCaseComps';
import { persistedInventoryUpdatesRef } from './CaseReviewHelpers/persistedInventoryUpdates';
import { clearCompLookupCache } from '../../common/useCompLookups';
import { useLocation } from 'react-router-dom';
import { getIFMVUtil } from '../../utils/getAVUtil';
import logger from '../../utils/logger';
import { useSelectInventorySale } from './useSelectInventorySale';


// Note: Adjustment calculation logic has been moved to src/utils/adjustmentCalculator.js
// Note: AdjustmentCell component removed - functionality now handled by unified adjustment calculator

export const TimeAdjustmentCell = ({ subject, getTimeAdjustmentSettings, IS_SALE_RUN }) => {
  

  // Call hooks at the top level
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const getEvalDate = getNegotiationObj?.evalDate;

  // Check if there's an override value that should take precedence
  const overrideValue = getTimeAdjustmentSettings?.timeAdjustmentValue;
  
  // If there's an override value, use it in the calculation
  if (overrideValue) {
    try {
      // Default value if the timeadjs don't exist === saledate
      const key = getTimeAdjustmentSettings?.saleDateType === 'contract' ? 'ContractDate' : 'SaleDate';
      const contractDate = subject?.[key] ? new Date(subject[key]) : null;
      const evalDate = getEvalDate ? new Date(getEvalDate) : null;
      
      
      if (!contractDate || isNaN(contractDate)) {
        return <div className="flex group justify-end h-full relative text-gray-500 italic"></div>;
      }

      if (!evalDate || isNaN(evalDate)) {
        return <div className="flex group justify-end h-full relative text-gray-500 italic">No Eval Date</div>;
      }

      // Calculate the difference in days
      const diffInTime = evalDate.getTime() - contractDate.getTime();
      const diffInDays = diffInTime / (1000 * 3600 * 24);

      // Calculate the percentage of the year
      const percentageOfYear = diffInDays / 365;

      // Apply the adjustment based on settings
      const adjustmentRate = parseFloat(overrideValue);
      if (isNaN(adjustmentRate)) {
        return <div className="flex group justify-end h-full relative text-gray-500 italic" role="gridcell">Set Value</div>;
      }

      // Get the correct price fields based on run type
      const subjectPriceField = IS_SALE_RUN ? 'SalePrice' : 'IFMV';
      const compAdjPriceField = IS_SALE_RUN ? 'adj_price' : 'adj_IFMV';
      const subjectPrice = parseFloat(subject[subjectPriceField]) || 0;
      const currentAdjPrice = parseFloat(subject[compAdjPriceField]) || 0;
      
      let adjustmentValue;
      let adjustedPrice;
      
      // Check if the override value is a percentage rate (0-100) or a dollar amount
      if (adjustmentRate <= 100 && adjustmentRate >= 0) {
        // It's a percentage rate - calculate based on time difference
        adjustmentValue = subjectPrice * (percentageOfYear * (adjustmentRate / 100));
        adjustedPrice = currentAdjPrice + adjustmentValue;
        
      } else {
        // It's a dollar amount - use it directly as the time adjustment
        adjustmentValue = adjustmentRate;
        adjustedPrice = currentAdjPrice + adjustmentValue;
        
      }


      // Render the adjusted price using the override rate
      return (
        <div className="flex group justify-end h-full relative" role="gridcell">
          {Math.round(adjustedPrice).toLocaleString()}
        </div>
      );
    } catch (error) {
      // Render error state
      logger.error(error.message);
      return (
        <div className="flex group justify-end h-full relative text-red-500">
          {error.message}
        </div>
      );
    }
  }

  // If no override, check if there's an existing time adjustment value in the subject
  const timeAdjValue = subject?.TimeAdjSalePrice || subject?.time_adj;
  
  if (timeAdjValue) {
    return (
      <div className="flex group justify-end h-full relative" role="gridcell">
        {timeAdjValue.toLocaleString()}
      </div>
    );
  }

  // If no existing value and no override, show "Set Value"
  return (
    <div className="flex group justify-end h-full relative text-gray-500 italic" role="gridcell">
      Set Value
    </div>
  );
};

// You have object references which recreate on each compchange that cause downstream rerenders (ie: in table toolbar to clac avg price)
// Note to self, you need to ensure that your function references are dependencies in your callbacks.
// Just traced down a dumbass bug because of this.
// Fixes for this code - using internal grid ref works very well and is more performant than constantly re-rendering the grid.
// however, yoour code is radically inefficeint because of the legacy structure of the components. This shoul dbe rebuilt, and streamlined
// many diff versions of mui's selection models could be used to make this more performant.
// we should also probably process the entire data object and just render the rows, instead of recreating on every comp change.
const CaseReview = ({
  originalCompObject,
  addCompNotInResponseCallback,
  originalCaseObject,
  originalOptimizedObject,
  resetUpdateArrayCallback,
  compStreamObject,
  updatedArray,
  handleUpdateStateCallback,
  savedCallbackRef,
  compErrors,
  setCompErrors,
}) => {  // update baths field to be "half baths", full baths, and total baths which is displayed by default.
  // logger.log('CaseReview component rendered', originalCompObject);
  // logger.log('originalOptimizedObject', originalOptimizedObject);
  // logger.log('originalCaseObject', originalCaseObject);

  // This component is poorly optimized. this can be observed if you try to rapidly clickthrough the comps.
  // This issue is made worse by the home rerendering on view swap - which is dumb.
  const {user} = useUser();
  const isTrainingUser = user?.userAccess?.level === 'Training';
  const { pickSale, dialog: inventorySaleDialog } = useSelectInventorySale();
  const getReportType = usePersistedDataStore((state) => state.reportType); // report type for What kind of run it was
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj)
  const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj)
  
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]); 
  const comp = useMemo(() => ( parseInt(queryParams.get('comp')) - 1 || 0), [queryParams]);

  const [view, setView] = useState('regular'); // changed view to be a local state here.


  const compObject = useMemo(() => originalCompObject[comp], [originalCompObject, comp]);
  // const compObject = originalCompObject[comp]
  const getTimeAdjustmentSettings = usePersistedDataStore((state) => state.timeAdjustmentSettings);
  const getEvalDate = usePersistedDataStore((state) => state.evalDate);
  
  // Flag to determine if we're using sale price fields or IFMV fields
  const IS_SALE_RUN = getReportType !== 'assessment';
  
  // Helper functions to get the correct price fields based on run type
  // Convert helper functions to constants to prevent memo invalidation
  const subjectPriceField = IS_SALE_RUN ? 'SalePrice' : 'IFMV';
  const compPriceField = IS_SALE_RUN ? 'SalePrice' : 'IFMV';
  const compAdjPriceField = IS_SALE_RUN ? 'adj_price' : 'adj_IFMV';
  const getCompPrice = (row) => parseFloat(row[compPriceField]) || 0;
  
  // Have this be an object, and have the object get updated from your dialog onsave.
  // Should memoize these to keep stable references.
  const caseObject = useMemo(() => originalCaseObject[comp], [originalCaseObject,comp]);
  const optimizedObject = useMemo(() => originalOptimizedObject[comp], [originalOptimizedObject,comp]);
  
  // Check if the case is settled based on negotiations
  const isSettled = useMemo(() => {
    return caseObject ? isCaseSettled(caseObject) : false;
  }, [caseObject]);
  
  
  // Memoize caseId to prevent unnecessary timer resets
  const caseId = useMemo(() => caseObject?.parcel_id, [caseObject?.parcel_id]);
  // Native identity: parcel_id (assessment) or TransactionID (sales). Do not parcel-map here —
  // mapping dual-selected every sale on the same parcel. Map/geo convert at the boundary only.
  const savedComps = useMemo(
    () => Object.values(caseObject?.Comps || {}).filter((id) => id != null && id !== ''),
    [caseObject?.Comps],
  );
  const townCompParcelIds = useMemo(
    () => Object.values(caseObject?.TownComps ?? {}),
    [caseObject?.TownComps]
  );
  const townCompSet = useMemo(
    () => new Set((townCompParcelIds || []).filter(Boolean)),
    [townCompParcelIds]
  );
  // i think now that you've moved to the single run framework, this isnt really needed.
  const taxYear = useMemo(() => {
    return parseInt(queryParams.get('TaxYear')) || getNegotiationObj.TaxYear; // Correctly fetch TaxYear and handle NaN
  }, [queryParams]);
  const rarHistory = useRarHistory(getNegotiationObj?.MuniCode);
  const village = queryParams.get('village') || 'All';
  const adjustmentRef = useRef({});
  const [rowUpdate, setRowUpdate] = useState(false);
  const [selectedComps, setSelectedComps] = useState(null);
  const [filteredParcelIds, setFilteredParcelIds] = useState(null);
  const [mapFilteredParcelIds, setMapFilteredParcelIds] = useState(null);

  // Presentation mode state
  const [presentationMode, setPresentationMode] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showTable, setShowTable] = useState(true);
  const [mapHeightPercent, setMapHeightPercent] = useState(45);
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const splitContainerRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const isResizingRef = useRef(false);
    // Use this state to 
    const [compObjectToDisplay, setCompObjectToDisplay] = useState(null);
    const memoizedRowUpdate = useMemo(() => rowUpdate, [rowUpdate]);
    const getAdjustments = useCompStore((state) => state.adjustments);
  // Menu state

    // Leaving it for now since negligible performance gain on this rerendering optimization.
    const [caseNotesChanged, setCaseNotesChanged] = useState(false);
    const [offerValueChanged, setOfferValueChanged] = useState(false);
    
    // the comp object that the user selects the assessor comps they want to analyze.
    const [negotiationComps, setNegotiationComps] = useState([]);
    const [negotiationCompsTouched, setNegotiationCompsTouched] = useState(false);
    const markNegotiationCompsTouched = useCallback(() => setNegotiationCompsTouched(true), []);
    // need a handler for updating the negotiation comps

  const townNegotiationComps = useMemo(
    () => negotiationComps.filter((c) => c?.parcel_id && townCompSet.has(c.parcel_id)),
    [negotiationComps, townCompSet]
  );

  // Add callback for map filter changes
  const handleMapFilterChange = useCallback((filteredIds) => {
    // Only update if the arrays are different (comparing values, not references)
    // logger.log('filteredIds', filteredIds)
    if (!mapFilteredParcelIds || 
        mapFilteredParcelIds.length !== filteredIds.length || 
        !mapFilteredParcelIds.every((id, index) => id === filteredIds[index])) {
      // logger.log('setting mapFilteredParcelIds', filteredIds)
      setMapFilteredParcelIds(filteredIds);
    }
  }, [mapFilteredParcelIds]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    const mapEl = mapWrapperRef.current;
    if (!container || !mapEl) return;
    isResizingRef.current = true;
    let currentPct = mapHeightPercent;
    const onMove = (moveEvent) => {
      const rect = container.getBoundingClientRect();
      const y = moveEvent.clientY - rect.top;
      const pct = (y / rect.height) * 100;
      currentPct = Math.min(80, Math.max(20, pct));
      mapEl.style.height = `${currentPct}%`;
    };
    const onEnd = () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setMapHeightPercent(currentPct);
      if (currentPct <= 20) {
        setIsMapCollapsed(true);
      } else {
        setIsMapCollapsed(false);
      }
      window.dispatchEvent(new Event('resize'));
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }, [mapHeightPercent]);

  useEffect(() => {
    if (!showMap || isMapCollapsed) return;
    const t = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(t);
  }, [showMap, isMapCollapsed]);

  const handleExpandMap = useCallback(() => {
    setIsMapCollapsed(false);
    setMapHeightPercent(45);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }, []);

  const handleFilteredRowsChange = useCallback((filteredIds) => {
    // logger.log('handleFilteredRowsChange called with filteredIds:', filteredIds)
    // Only update if the filtered IDs are different from current state
    if (JSON.stringify(filteredParcelIds) !== JSON.stringify(filteredIds)) {
      // logger.log('setting filteredParcelIds IN CASEREVIEWWW', filteredIds)
      setFilteredParcelIds(filteredIds);
    }
  }, [filteredParcelIds]);

  // Helper function to apply filters to rows while preserving selected comps
  const applyFiltersToRows = useCallback((rows, selectedComps = [], userFilterSettings = {}, savedComps = [], subjectRow = null) => {
    
    if (rows.length === 0) {
      return {
        filteredRows: [],
        filteredOutCount: 0,
        selectedCompsFilteredOut: [],
        filteredParcelIds: []
      };
    }

    // Use provided subject row or fall back to finding it in rows
    const subject = subjectRow || rows.find(row => row.isSubject)
    
    const filteredRows = [];
    const selectedCompsFilteredOut = [];
    const filteredParcelIds = [];
    let filteredOutCount = 0;
    
    // Track filter reasons for summary logging
    const filterReasonCounts = {
      'OutsideDateRange': 0,
      'AcreageFilter': 0,
      'SquareFeetFilter': 0,
      'GrossAdjFilter': 0,
      'NetAdjFilter': 0
    };
    
    // Create set for quick lookup of selected comps (TransactionID on sales, parcel otherwise)
          rows.forEach(row => {
        const parcelId = row.parcel_id || row.id;
        const isSelected = selectionSetHasRow(selectedComps, row, IS_SALE_RUN);
      
      // Check if this is the subject row - check both isSubject property and if it matches the subjectRow parameter
      const isSubjectRow = row.isSubject || (subject && subject.parcel_id === parcelId);
      
      // Check if row passes filters based on existing error data
      const passesFilter = !row.errors || row.errors.length === 0;
      // let errors = [];
      
      // Handle subject row specially - always include it first with proper numbering and error checking
      if (isSubjectRow) {
        const updatedRow = {
          ...row,
          '#': 0
        };
        
        // Error data is already in the row from createRows
        
        filteredRows.unshift(updatedRow); // Add to beginning of array
        filteredParcelIds.push(parcelId);
        return; // Skip the rest of the logic for subject row
      }
      
      // Always include selected comps, but track if they don't match filter
      if (isSelected) {

        
        // Add filter errors to existing errors array for selected comps that violate filters
        const updatedRow = {
          ...row,
          '#': filteredRows.length + 1 // Number based on position in filtered list
        };
        
        // Error data is already in the row from createRows
        
        filteredRows.push(updatedRow);
        // Add selected comps to filteredParcelIds for map filtering
        filteredParcelIds.push(parcelId);
        if (row.errors && row.errors.length > 0) {
          selectedCompsFilteredOut.push(parcelId);
        }
      } else if (!row.errors || row.errors.length === 0) {
        // Include non-selected comps that pass the filter
        filteredRows.push({
          ...row,
          '#': filteredRows.length + 1 // Number based on position in filtered list
        });
        // Add to filteredParcelIds if it's a saved comp or passes filter
        filteredParcelIds.push(parcelId);
      } else {
        // For saved comps that don't pass filter, still include them in filteredParcelIds
        // but don't add them to filteredRows (they won't show in table)
        if (selectionSetHasRow(savedComps, row, IS_SALE_RUN)) {
          filteredParcelIds.push(parcelId);
          // Add filter errors to saved comps that don't pass filter
          const updatedRow = {
            ...row,
            '#': filteredRows.length + 1 // Number based on position in filtered list
          };
          
          // Error data is already in the row from createRows
          
          filteredRows.push(updatedRow);
        } else {
          // Track filtered out comps
          filteredOutCount++;
        }
      }
    });
    
    // Fix numbering for all rows to ensure no duplicates
    filteredRows.forEach((row, index) => {
      row['#'] = index + 1;
    });
    
    // Log only the summary counts
    const activeFilters = Object.entries(filterReasonCounts)
      .filter(([_, count]) => count > 0)
      .map(([reason, count]) => `${reason}: ${count}`)
      .join(', ');
    
    if (activeFilters) {
      logger.log(`Filtered out: ${activeFilters} | Total: ${filteredOutCount} | Selected violations: ${selectedCompsFilteredOut.length}`);
    }
    
    return {
      filteredRows,
      filteredOutCount,
      selectedCompsFilteredOut,
      filteredParcelIds
    };
  }, [getCompPrice, getAdjustments, IS_SALE_RUN]);

  const showRefreshButton = updatedArray[comp] === false;

  // i hate this useeeffect flow
  // Reset selected comps when comp changes
  useEffect(() => {
    logger.log('the useeffect oncomp change is running')
    handleCompChange();
  }, [comp]);


  // Update the reviewDetails memo to use more specific dependencies
  const reviewDetails = useMemo(() => {
    const currentCase = getNegotiationObj.cases[comp];
    
    return {
      manualReview: currentCase?.ManualReview || false,
      reviewedBy: currentCase?.ReviewedBy || 'Not reviewed',
      lastReviewDate: formatDateWithoutTimezone(currentCase?.RunDate)
    };
  }, [
    getNegotiationObj.cases[comp]?.ManualReview,
    getNegotiationObj.cases[comp]?.ReviewedBy,
    getNegotiationObj.cases[comp]?.RunDate,
    comp
  ]);

  // Move memoizedSelectedAdjustments declaration here, before calculateAdjustments uses it
  const memoizedSelectedAdjustments = useMemo(() => caseObject?.MiscAdjustments || {}, [caseObject]);

  const memoizedOptimizedObject = useMemo(() => {
    return optimizedObject;
  }, [optimizedObject]);

  const [isSaving, setIsSaving] = useState(false); // state to set loading on the grid (doesn't work with pinned but thats a minor bug)

  const handleSetSaving = useCallback((value) => {
    setIsSaving(value);
  }, []);

  // Helper function to reorder uids with current parcel as 0th index, makes sure the next comp up gets run second, etc.
  const reorderUidsWithCurrentFirst = useCallback((uids, currentIndex) => {
    if (!uids || uids.length === 0 || currentIndex < 0 || currentIndex >= uids.length) {
      return uids;
    }
    
    // Start with current parcel at index 0
    const reorderedUids = [uids[currentIndex]];
    
    // Add the rest of the parcels in order, wrapping around
    for (let i = 1; i < uids.length; i++) {
      const nextIndex = (currentIndex + i) % uids.length;
      reorderedUids.push(uids[nextIndex]);
    }
    
    return reorderedUids;
  }, []);

  const isCompUpdated = useMemo(() => {
    return updatedArray.length === 0 ? true : updatedArray[comp];
  }, [updatedArray, comp]);
  
  // Polling logic moved to dedicated UpdatePollingComponent to prevent unnecessary re-renders

  const { processStreamWithWorker, getDetailedProgress, resetProcessingState } = useStreamProcessorWorker();

  const unsavedChangesRef = useRef({
    originalSubject: {},
    unsavedRows: {},
    rowsBeforeChange: {},
    unsavedSubject: {},
  });

  // remove the property tooltip display by setting compObjectToDisplay to null
  const updateSelectedComps = useCallback((newSelection, preventCompDisplayReset = false) => {
    setSelectedComps(newSelection);
    setCompObjectToDisplay(null);
  }, []);

  const memoizedSelectedComps = useMemo(() => selectedComps, [selectedComps]);
  const activeSelectionIds = memoizedSelectedComps ?? savedComps;

  // Map UI is parcel-keyed; convert identity ↔ parcels only at this boundary.
  const selectedCompsForMap = useMemo(
    () => mapCompIds(compObject, activeSelectionIds, getReportType, true),
    [compObject, activeSelectionIds, getReportType],
  );

  const updateSelectedCompsFromMap = useCallback((newParcelSelection, preventCompDisplayReset = false) => {
    if (!IS_SALE_RUN) {
      updateSelectedComps(newParcelSelection, preventCompDisplayReset);
      return;
    }
    const sheet = view === 'optimized' ? optimizedObject : compObject;
    const current = activeSelectionIds;
    const currentParcels = mapCompIds(sheet, current, getReportType, true).map(normalizeCompId);
    const nextParcels = new Set((newParcelSelection || []).map(normalizeCompId));
    const removed = new Set(currentParcels.filter((p) => !nextParcels.has(p)));
    const added = [...nextParcels].filter((p) => !currentParcels.includes(p));

    let next = current.filter((id) => {
      const idx = findCompIndex(sheet, id, getReportType);
      if (idx < 0) return true;
      return !removed.has(normalizeCompId(sheet.parcel_id?.[idx]));
    });
    for (const p of added) {
      (sheet?.parcel_id || []).forEach((pid, i) => {
        if (i === 0 || normalizeCompId(pid) !== p) return;
        const txn = sheet.TransactionID?.[i];
        if (txn == null || txn === '') return;
        if (!next.some((id) => normalizeCompId(id) === normalizeCompId(txn))) next.push(txn);
      });
    }
    updateSelectedComps(next, preventCompDisplayReset);
  }, [IS_SALE_RUN, view, optimizedObject, compObject, activeSelectionIds, getReportType, updateSelectedComps]);



  // Memoized compObject recalculation
  const memoizedCompObject = useMemo(() => {
    // Always use the original compObject - don't reconstruct it for negotiation view
    // The negotiation view should just filter the rows, not modify the data structure
    return compObject;
  }, [compObject]);

  // Derive timer props as primitives with minimal deps to prevent cascading re-renders
  const subjectIFMV = useMemo(() => getIFMVUtil(memoizedCompObject, 0) ?? 0, [memoizedCompObject?.IFMV?.[0]]);



  const viewCallback = useCallback((newValue, clearNegotiation = false) => {
    const newView =
      newValue === 0 ? 'regular'
      : newValue === 1 ? 'optimized'
      : 'town';
    setView(newView);
    
    // Only clear negotiation comps if explicitly requested
    if (clearNegotiation) {
      setNegotiationComps((prev) => (prev.length > 0 ? [] : prev));
    }
  }, []);

  // REmove the property info popup display
  const handleCompViewClose = useCallback(() => {
    // logger.log('[compObjectToDisplay] set to null (handleCompViewClose)');
    setCompObjectToDisplay(null);
  }, []);

  // extract yourr subject and comps
  const subject = useMemo(() => {
    if (!memoizedCompObject) return {};
    const subjectData = Object.fromEntries(
      Object.entries(memoizedCompObject).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value])
    );
    return subjectData;
  }, [memoizedCompObject]);

  const subjectParcelId = subject?.parcel_id;

  const persistInventoryUpdates = useCallback((unsaved, records) => {
    const skip = {
      pid: 1, id: 1, parcel_id: 1, selected: 1, selectedRowNumber: 1, isSelected: 1,
      errors: 1, errorMessages: 1, PropertyInfo: 1, isSubject: 1, priceAdjustments: 1,
      trainingSelectedNumber: 1, trainingUserOrdinal: 1,
    };
    const merge = (pid, fields) => {
      if (pid == null || !fields) return;
      const cleaned = {};
      for (const [k, v] of Object.entries(fields)) {
        if (skip[k] || (v != null && typeof v === 'object')) continue;
        cleaned[k] = v;
      }
      if (!Object.keys(cleaned).length) return;
      const key = String(pid);
      persistedInventoryUpdatesRef.current[key] = {
        ...persistedInventoryUpdatesRef.current[key],
        ...cleaned,
      };
    };
    (records || []).forEach((rec) => merge(rec?.pid || rec?.parcel_id, rec));
    const subj = unsaved?.unsavedSubject;
    if (subj) merge(subj.pid || subj.parcel_id || subjectParcelId, subj);
    Object.entries(unsaved?.unsavedRows || {}).forEach(([rowId, fields]) => {
      merge(fields?.pid || fields?.parcel_id || rowId, fields);
    });
  }, [subjectParcelId]);

  // Memoize the optimized components
  const optimizedComps = useMemo(() => {
    // const workingOptimizedObject = cloneDeep(optimizedObject);
    // This needs to perform additional filtering to remove the red comps too whenever we are aready to do that
    if (!memoizedOptimizedObject) return {};
    const currentOptimizedObject = Object.fromEntries(
      Object.entries(memoizedOptimizedObject).map(([key, value]) => [key, Array.isArray(value) ? value.slice(1) : value])
    );
    return currentOptimizedObject
  }, [memoizedOptimizedObject]);

  const regularComps = useMemo(() => {
    if (!memoizedCompObject) return {};
    return Object.fromEntries(
      Object.entries(memoizedCompObject).map(([key, value]) => [key, Array.isArray(value) ? value.slice(1) : value])
    );
  }, [memoizedCompObject]);

  // Add a new row to negotiation comps (append after existing comps, before blank row)
  const updateNegotiationComps = useCallback(
    (newRow) => {
      const incoming = (Array.isArray(newRow) ? newRow : [newRow]).filter((row) => row?.parcel_id);
      if (!incoming.length) return;
      setNegotiationComps((prev) => {
        let next = prev;
        incoming.forEach((row) => {
          const existingIndex = next.findIndex((item) => item.parcel_id === row.parcel_id);
          if (existingIndex !== -1 && JSON.stringify(next[existingIndex]) === JSON.stringify(row)) return;
          const filtered = next.filter((item) => item.parcel_id !== row.parcel_id);
          const blankRowIndex = filtered.findIndex(
            (item) =>
              (!item.Address || item.Address === '') &&
              (!item.PropertyInfo || !item.PropertyInfo.Address || item.PropertyInfo.Address === '')
          );
          if (blankRowIndex !== -1) {
            const result = [...filtered];
            result.splice(blankRowIndex, 0, row);
            next = result;
          } else {
            next = [...filtered, row];
          }
        });
        return next;
      });
    },
    []
  );

  const handleDeleteProperty = useCallback((property) => {
    setNegotiationCompsTouched(true);
    setNegotiationComps((prev) => prev.filter((row) => row.parcel_id !== property.parcel_id));
  }, []);

    
// Now you can use optimizedComps and regularComps independently
  const comps = useMemo(() => {
    if (view === 'optimized') {
      return optimizedComps;
    }else if(view === 'town'){
      return negotiationComps;
    }
    return regularComps;
  }, [view, optimizedComps, regularComps, negotiationComps]);

  const tableProps = useMemo(() => ({ // custom table props for case review vverrsion of table.
    density: 'compact', // how closely spaced
    disableDensitySelector: true, // remove this option from the table header
    checkboxSelection: false,
    compTable: true, // in lower levvel MuiTable use this to set the backgrorund color.
    disableRowSelectionOnClick: true, // this will disable row selection on click
    disableAutosize: true,
    hideFooter: true, // remove the pagination / footer.
  }), []);
  // comps to fill in from callback. Default value should be to pass in ur regular saved.

  const handleSetPropertyInfo = useCallback((comp, subject = false) => {
    if (!comp) return;
    const overlay = persistedInventoryUpdatesRef.current[String(comp.parcel_id || comp.pid)];
    const merged = overlay ? { ...comp, ...overlay } : comp;
    merged.subject = subject;
    setCompObjectToDisplay({ ...merged, ...pickMapInventory(merged) });
  }, []);

    // Funtion to actually update the inventory.
    const handleProcessDBUpdate = useCallback(async (responseObject = getNegotiationObj, resetArray = true) => {
      let skipRerunOnInventoryUpdate = getNegotiationObj?.skipRerunOnInventoryUpdate === true;
      try {
        skipRerunOnInventoryUpdate =
          skipRerunOnInventoryUpdate ||
          JSON.parse(localStorage.getItem('advancedSettings') || '{}')?.skipRerunOnInventoryUpdate === true;
      } catch (e) {
        // keep store value
      }

      if (!skipRerunOnInventoryUpdate) {
        logger.log('🔄 Resetting stream processing state before new inventory update');
        resetProcessingState();
        clearCompLookupCache();
      }

      if (typeof responseObject === 'string') {
        logger.log('no resp obj, means this is a NAL sale flagged run');
        logger.log(getNegotiationObj);
    
        // Add safety check for getNegotiationObj and uids
        if (!getNegotiationObj || !Array.isArray(getNegotiationObj.uids)) {
          logger.error('Invalid negotiation object or uids array');
          return;
        }
        // Reorder uids with current parcel as 0th index, then next parcels in order
        const uidArray = reorderUidsWithCurrentFirst(getNegotiationObj.uids, comp);
        responseObject = {
          ...getNegotiationObj, // Start with the current negotiation object - this preserves ALL fields
          NALSaleRunFlag: 1, // Add the NAL sale flag
          uids: uidArray, // Add/override the uids field
          updatedSubject: uidArray[0], // Add the updated subject field
          Records: [{ pid: responseObject }], // Add the Records field
          // For NAL sale flagged runs, only include the specific case being updated
          // cases: [getNegotiationObj.cases[comp]], // Only include the current case
        };
      } else {
        // If responseObject is not a string, ensure it has all the negotiation object fields
        // For inventory update, reorder uids with current parcel as 0th index, then next parcels in order
        const uidArray = reorderUidsWithCurrentFirst(getNegotiationObj.uids, comp);
        // const uidArray = [currentUid];
        responseObject = {
          ...getNegotiationObj, // Preserve all negotiation object fields
          ...responseObject, // Override with any specific fields from the passed responseObject
          uids: uidArray,
          updatedSubject: uidArray[0],
        };
      }

      if(resetArray && !skipRerunOnInventoryUpdate){
        compStreamObject.current.updated = new Array(responseObject.cases.length).fill(false);
      }
      // This needs to be set at the start of the function
      // Clone the `uids` array and assign it to `originalPidOrder`
      // construct this uids array from the cases.parcel_id values for each thing
      const originalPidOrder = responseObject.cases.map((caseObj) => caseObj.parcel_id);
      responseObject.originalPidOrder = originalPidOrder;
    
      // Await resetting the update array callback
      // logger.log('resetting update array callback', responseObject.cases.length)
      // logger.log('resetArray parameter:', resetArray);
      if (!skipRerunOnInventoryUpdate) {
        await resetUpdateArrayCallback(responseObject.cases.length);
        responseObject.inventoryUpdateRun = 1;
        responseObject.manualReview = 1;
      }
    
      try {
        const baseUrl = getBaseURL();
        logger.log('responseObject records being sent in', responseObject.Records)

        // ADDED THIS CHECKING FUNCTION TEMPORARILY TO TROUBLESHOOT UPDATE INVENTORY
        // Check if the keys in Records match the expected backend keys
        const checkKeyMismatches = (records) => {
          if (!records || !Array.isArray(records)) {
            return;
          }

          // Expected backend keys from mappings file (excluding pid which is handled separately)
          const expectedKeys = AVTN_BACKEND_UPDATE_KEYS;

          const allMismatches = [];
          
          records.forEach((record, index) => {
            if (!record || typeof record !== 'object') {
              return;
            }

            const actualKeys = Object.keys(record);
            // Only check for extra keys that don't match expected schema
            // Don't flag missing keys since users may only provide a subset
            const extraKeys = actualKeys.filter(key => !expectedKeys.includes(key) && key !== 'pid');

            if (extraKeys.length > 0) {
              allMismatches.push({
                recordIndex: index,
                parcelId: record.pid || 'Unknown',
                extraKeys
              });
            }
          });

          // If there are mismatches, send notification email
          if (allMismatches.length > 0) {
            const userId = user?.userId;
            
            const emailBody = `
Key Mismatch Alert - Records Response Validation

Municipality: ${responseObject.MuniCode || 'Not specified'}
Tax Year: ${responseObject.TaxYear || 'Not specified'}
User ID: ${userId}
Total Records: ${records.length}
Records with Mismatches: ${allMismatches.length}

Expected Keys: ${expectedKeys.join(', ')}

Mismatch Details:
${allMismatches.map(mismatch => `
Record ${mismatch.recordIndex} (Parcel ID: ${mismatch.parcelId}):
- Extra Keys (not in expected schema): ${mismatch.extraKeys.join(', ')}
`).join('')}

This indicates a potential issue with the data structure being returned from the backend.
Please investigate the API response format and ensure it matches the expected schema.
            `;

            // Send notification email using the existing route
            fetch(`${baseUrl}/send_internal_email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                toAddress: ['jrburns@aventine.ai'],
                subject: 'Key Mismatch Alert - Records Response Validation',
                message: emailBody
              })
            }).catch(error => {
              logger.error('Failed to send key mismatch notification:', error);
            });

            logger.warn(`Schema mismatch detected: ${allMismatches.length} records have extra keys not in expected schema out of ${records.length} total records.`);
          }
        };

        // Check for key mismatches before sending to backend
        if (responseObject.Records) {
          checkKeyMismatches(responseObject.Records);
        }

        logger.log('response obj coming into update inventory', responseObject)


        // Need an onupdate function which will be called to process stream
        const updateInventoryResponse = await authorizedFetch(`${baseUrl}/update_inventory`, {
          method: "POST",
          body: JSON.stringify(responseObject),
        });

        // Check for HTTP error responses
        if (!updateInventoryResponse.ok) {
          logger.log('HTTP error response:', updateInventoryResponse);
          
          // Try to parse the error response to get the specific error message
          let errorMessage = 'Error processing update - see logs for details';
          try {
            const errorData = await updateInventoryResponse.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (parseError) {
            logger.error('Failed to parse error response:', parseError);
            // Use HTTP status text if JSON parsing fails
            errorMessage = `HTTP ${updateInventoryResponse.status}: ${updateInventoryResponse.statusText}`;
          }
          
          // Reset state before throwing error
          await resetUpdateArrayCallback(0);
          delete compStreamObject.current.updated;
          resetProcessingState();
          
          toast.error(`Server error: ${errorMessage}`);
          throw new Error(`Inventory update failed: ${errorMessage}`);
        }

        logger.log("Inventory updated successfully.");

        if (skipRerunOnInventoryUpdate) {
          persistInventoryUpdates(unsavedChangesRef.current, responseObject.Records);
          unsavedChangesRef.current = {
            originalSubject: {},
            unsavedRows: {},
            rowsBeforeChange: {},
            unsavedSubject: {},
          };
          setRowUpdate(!rowUpdate);
          toast.success('DB Inventory updated, per your settings algorithm was NOT re-run.');
          return;
        }

        setRowUpdate(!rowUpdate);

        logger.log("Starting stream processing...");
        logger.log('request obj going into knn:::')
        logger.log(responseObject)
            
        try {
          const clearUnsavedChanges = () => {
            logger.log('🧹 Clearing unsaved changes - KNN processing started');
            unsavedChangesRef.current = {
              originalSubject: {},
              unsavedRows: {},
              rowsBeforeChange: {},
              unsavedSubject: {},
            };
          };
          
          // wait 2 seconds before calling
          await new Promise(resolve => setTimeout(resolve, 10000));

          await fetchKNNModel({
            isInventoryUpdate: true,
            globalCompRef: null, // Not needed for inventory updates
            compStreamObject: compStreamObject,
            processStreamWithWorker: processStreamWithWorker,
            user: user,
            clearUnsavedChanges: clearUnsavedChanges
          });
          logger.log("KNN stream processing complete");
        } catch (knnError) {
          logger.error('Error during KNN processing:', knnError);
          // Reset processing state on error
          logger.log('🔄 Resetting stream processing state due to error');
          resetProcessingState();
          // Don't fail the entire operation if KNN fails, just log it
          toast.warning('Inventory updated successfully, but KNN model failed to run. Comps may not reflect latest changes.');
        }

      } catch (error) {
        logger.error('Error while updating inventory:', error);
        
        // Reset the updated array to empty array on any error
        await resetUpdateArrayCallback(0);
        delete compStreamObject.current.updated;
        
        // Reset stream processing state on error
        resetProcessingState();
        
        // Clear unsaved changes ref
        unsavedChangesRef.current = {
          originalSubject: {},
          unsavedRows: {},
          rowsBeforeChange: {},
          unsavedSubject: {},
        };
        
        // Determine error type and show appropriate message
        let errorMessage = 'Failed to update inventory. Please try again.';
        
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          // CORS or network error
          errorMessage = 'Network error: Unable to connect to server. Please check your connection and try again.';
        } else if (error.message.includes('CORS')) {
          // Specific CORS error
          errorMessage = 'CORS error: Server configuration issue. Please contact support.';
        } else if (error.message) {
          // Use the specific error message if available
          errorMessage = `Update failed: ${error.message}`;
        }
        
        toast.error(errorMessage, {
          autoClose: 5000,
          position: 'top-right'
        });
        
        // Force a re-render to update UI state
        setRowUpdate(!rowUpdate);
      }
    }, [getNegotiationObj, comp, resetUpdateArrayCallback, updatedArray, rowUpdate, handleSetSaving, reorderUidsWithCurrentFirst, persistInventoryUpdates]);


  const handleManualRefresh = useCallback(async () => {
    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
    const displayedId = subject?.parcel_id;
    const uid = (negotiationObj?.uids || []).find((id) => String(id) === String(displayedId))
      ?? negotiationObj?.cases?.find((item) => String(item?.parcel_id) === String(displayedId))?.parcel_id;
    if (uid == null || uid === '') {
      toast.error('No case to refresh');
      return;
    }
    logger.log('[case-refresh] start', { parcelId: String(uid) });
    const singleStream = { current: {} };
    await fetchKNNModel({
      isInventoryUpdate: true,
      globalCompRef: null,
      compStreamObject: singleStream,
      processStreamWithWorker,
      user,
      onlyUid: uid,
    });
    const regularHit = sheetForParcel(singleStream.current?.properties?.[0], uid);
    const optimizedHit = sheetForParcel(singleStream.current?.properties?.[1], uid);
    if (!regularHit.sheet && !optimizedHit.sheet) return;

    const store = usePersistedDataStore.getState();
    const cases = store.negotiationObj?.cases || [];
    const regularPlaced = replacedSheets(store.compSheet || [], cases, uid, regularHit.sheet);
    const optimizedPlaced = replacedSheets(store.optimizedComps || [], cases, uid, optimizedHit.sheet);
    const assignment = caseRefreshAssignmentLog({
      parcelId: uid,
      responseRegularIndex: regularHit.index,
      responseOptimizedIndex: optimizedHit.index,
      regularIndex: regularPlaced.index,
      optimizedIndex: optimizedPlaced.index,
      regularSheets: regularPlaced.sheets,
      optimizedSheets: optimizedPlaced.sheets,
    });
    logger.log('[case-refresh] assigned', assignment);
    if (!assignment.regularMatched && !assignment.optimizedMatched) {
      toast.error('Refreshed comps could not be matched to this case');
      return;
    }
    if (assignment.regularMatched) store.setCompSheet(regularPlaced.sheets);
    if (assignment.optimizedMatched) store.setOptimizedComps(optimizedPlaced.sheets);
    setRowUpdate((prev) => !prev);
    toast.success('Comps refreshed');
  }, [subject?.parcel_id, processStreamWithWorker, user]);
    // THIS function is where you set the value of the row, and use this state.
  const handleAddressClick = useCallback((comp) => {
    if (!comp) return;
    const formattedProperty = {
      address: comp.PropertyInfo?.Address || comp.Address,
      parcel_id: comp.parcel_id,
      salePrice: comp.SalePrice,
      saleDate: comp.SaleDate,
      adjustedPrice: comp[compAdjPriceField],
      ifmv: comp.IFMV,
      taxes: comp.Taxes,
      ...overlayMapInventory(pickMapInventory(comp), persistedInventoryUpdatesRef.current[String(comp.parcel_id)]),
      MLSNumber: comp.MLSNumber,
      Remarks: comp.Remarks,
      SDName: comp.SDName,
      SDCode: comp.SDCode,
      town: comp.Town,
      zip: comp.ZipCode,
      isSubject: comp.isSubject,
      selectedRowNumber: comp.selectedRowNumber,
      latitude: comp.latitude || comp.Latitude,
      longitude: comp.longitude || comp.Longitude
    };
    // logger.log('[compObjectToDisplay] set (handleAddressClick)', {
    //   parcel_id: formattedProperty.parcel_id,
    //   latitude: formattedProperty.latitude,
    //   longitude: formattedProperty.longitude,
    //   hasCoords: formattedProperty.latitude != null && formattedProperty.longitude != null,
    //   sourceCompKeys: comp ? Object.keys(comp).filter(k => ['Latitude', 'latitude', 'Longitude', 'longitude'].includes(k)) : [],
    // });
    setCompObjectToDisplay(formattedProperty);
  }, [compAdjPriceField]);

    // this is where a huge cost of your rendering is. Optimizing your render cell fucntions will save render time.
    // the caseobject and savedcomps cause this to recalc header and re-render grid between every comp change.



  // use this to trigger renders within the table - if there exists a selected adj for a parcelID, then display a green $ sign
  // next to sale px. See if this top level rerender is performant enough for now.
  const [displayModal, setDisplayModal] = useState(false);
  const toggleDisplayModal = useCallback(() => {

    // Check if user is trying to open the modal
    if (!displayModal) {
      // Validate that selected comps are saved before allowing modal to open
      const currentSelectedComps = selectedComps ?? savedComps;
      
      // Check if selected comps are different from saved comps
      if (savedComps.length !== currentSelectedComps.length || 
          !savedComps.every((pid, index) => pid === currentSelectedComps[index])) {
        toast.error('Save new comps before adding misc adjustments');
        return; // Don't open the modal
      }
    }
    
    setDisplayModal((prev) => !prev);
  }, [displayModal, selectedComps, savedComps]);

    // Toggle adjustment display modal
    const handleAddAdj = useCallback(() => {
      toggleDisplayModal()
    }, [toggleDisplayModal]);

  // Delete your misc adjustments.
  const handleDeleteAdj = useCallback(async (adjData) => {
    logger.log('deleting adjustment');
    try {
        const deleteAdjResponse = await axiosInstance.post('/delete_misc_adj', {
            PID: caseObject.parcel_id,
            adjustmentData: adjData,
            MuniCode: getNegotiationObj.MuniCode,
            TaxYear: taxYear,
            // villageFlag: village === 'All' ? 0 : 1,
            UserId: user?.userId || null,
            TrainingFlag: isTrainingUser ? 1 : 0,
            ReportType: getReportType,
        });

        // The delete adjustment thing comes back with the value field still.
        // DOUBLE CHECK THIS THAT IT HAS THE CORRECT VALUES IN THE RESPONSE (removing the one you ust killed)
        logger.log(deleteAdjResponse.data);

        // Check for successful response
        if (deleteAdjResponse.data && deleteAdjResponse.data[0] !== false) {
            toast.success(`Successfully deleted misc adjustment ${adjData.name}`);
            logger.log('remaining adjustments:', deleteAdjResponse.data[1]);
            const remainingMiscAdjustments = deleteAdjResponse.data[1];
            // setSelectedAdjustments(remainingMiscAdjustments);
            // Clone the current state of cases to avoid mutating the original state
            const updatedCases = [...getNegotiationObj.cases];
            
            // Clone the specific case to update
            const currentCase = { ...updatedCases[comp] };
            
            // Update the MiscAdjustments field within the current case
            const updatedCase = {
              ...currentCase,
              MiscAdjustments: { ...remainingMiscAdjustments }, // Replace MiscAdjustments with the new adjustments
            };

            // Reassign the updated case back into the cases array
            updatedCases[comp] = updatedCase;

            // Final batched update to NegotiationObj
            const updatedNegotiationObj = {
              ...getNegotiationObj,
              cases: updatedCases,
            };

            // Update the state with the new NegotiationObj
            setNegotiationObj(updatedNegotiationObj);
            
            // Force recalculation of adjusted sale prices
            setRowUpdate(!rowUpdate);
        } else {
            // Handle the case where the response is false or not successful
            toast.error('Failed to delete miscellaneous adjustment. Please try again.');
        }
    } catch (error) {
      logger.error('Error deleting adjustment:', error);
    }
  }, [taxYear, village, getNegotiationObj, caseObject, comp, rowUpdate, getReportType]);

  // Add misc adjustment.
  // THIS IS NOT ERRORING HOW IT IS MEANT TO, need to revisit
  const handleAddAdjustment = useCallback(async (adjustmentData) => {
    logger.log(adjustmentData);
    
    // Check if at least one property is selected
    const hasSelectedProperty = adjustmentData.selectedProperties.some(value => value === 1);
    if (!hasSelectedProperty) {
      toast.error('Please select at least one property for the adjustment.');
      return;
    }

    const adjustmentDataObject = {
        Name: adjustmentData.adjustmentName,
        Adjustment: adjustmentData.adjustmentValue,
        AdjustmentType: adjustmentData.adjustmentType === '%' ? 'percentage' : 'numerical', // Change this to be "numerical or percentage based on the value"
        Values: adjustmentData.selectedProperties,
    };

    logger.log('Adding adjustment');

    try {
        const miscAdjustmentResponse = await axiosInstance.post('/add_misc_adj', {
            PID: caseObject.parcel_id,
            adjustmentData: adjustmentDataObject,
            MuniCode: getNegotiationObj.MuniCode,
            TaxYear: taxYear,
            // villageFlag: village === 'All' ? 0 : 1,
            UserId: user?.userId || null,
            repID: caseObject.repId || null,
            TrainingFlag: isTrainingUser ? 1 : 0,
            ReportType: getReportType,
        });

        // Check for successful response
        if (miscAdjustmentResponse.data && miscAdjustmentResponse.data !== false) {
            toast.success('Successfully added misc adjustment');
            // setSelectedAdjustments(miscAdjustmentResponse.data);
            // Here need to update your negotiation object too:
            const updatedCases = [...getNegotiationObj.cases];
            
            // Clone the specific case to update
            const currentCase = { ...updatedCases[comp] };
            
            // Update the MiscAdjustments field within the current case
            const updatedCase = {
              ...currentCase,
              MiscAdjustments: { ...miscAdjustmentResponse.data }, // Replace MiscAdjustments with the new adjustments
            };

            // Reassign the updated case back into the cases array
            updatedCases[comp] = updatedCase;

            // Final batched update to NegotiationObj
            const updatedNegotiationObj = {
              ...getNegotiationObj,
              cases: updatedCases,
            };

            // Update the state with the new NegotiationObj
            setNegotiationObj(updatedNegotiationObj);
            
            // Force recalculation of adjusted sale prices by updating rowUpdate state
            setRowUpdate(!rowUpdate);
            
            // This will cause the map to update as well since memoizedLatLongs depends on rowUpdate
        } else {
            // Handle the case where the response is false or not successful
            toast.error('Failed to add miscellaneous adjustment. Please try again.');
        }
    } catch (error) {
        logger.error('Error adding adjustment:', error);
    }
  }, [caseObject, taxYear, village, rowUpdate, getNegotiationObj, comp, getReportType, user, isTrainingUser]);
  

  // Handle offer value changes from toolbar
  const handleOfferValueChange = useCallback((newOfferValue) => {
    if (newOfferValue !== caseObject?.OfferValue) {
      setOfferValueChanged(true);
    }
  }, [caseObject?.OfferValue]);

  // Handle case notes changes from toolbar
  const handleCaseNotesChange = useCallback((newCaseNotes) => {
    if (newCaseNotes !== caseObject?.CaseNotes) {
      setCaseNotesChanged(true);
    }
  }, [caseObject?.CaseNotes]);

  // Memoized negotiation data for map (raw data, not processed)
  const memoizedNegotiationMapData = useMemo(() => {
    if (view === 'town') return townNegotiationComps;
    return [];
  }, [view, negotiationComps, townNegotiationComps]);

  // why do we still have this whatsoever? You should just pass your actual rows.
    // this function is only iterating over the regular comps. Is this a problem?
    const memoizedLatLongs = useMemo(() => {
      const parseCoordinates = (lat, lon) => ({
        latitude: typeof lat === 'string' ? parseFloat(lat) : lat,
        longitude: typeof lon === 'string' ? parseFloat(lon) : lon
      });

      // Handle town view - use comp arrays directly
      if (view === 'town') {
        const mapComps = townNegotiationComps;
        if (!mapComps || mapComps.length === 0) return [];

        // Start with subject from compObject
        const subject = {
          parcel_id: compObject?.parcel_id?.[0],
          id_number: 0,
          latitude: parseCoordinates(compObject?.Latitude?.[0], compObject?.Longitude?.[0]).latitude,
          longitude: parseCoordinates(compObject?.Latitude?.[0], compObject?.Longitude?.[0]).longitude,
          address: compObject?.Address?.[0],
          town: compObject?.Town?.[0] || null,
          zip: compObject?.ZipCode?.[0] || null,
          Remarks: compObject?.Remarks?.[0] || null,
          SDCode: compObject?.SDCode?.[0] || null,
          SDName: compObject?.SDName?.[0] || null,
          MLSNumber: compObject?.MLSNumber?.[0] || null,
          MLSPhotoFolder: compObject?.MLSPhotoFolder?.[0] || null,
          SalesWebScreenshot: compObject?.SalesWebScreenshot?.[0] || null,
          isSubject: true,
          selected: true,
          selectedRowNumber: 0,
          ...pickMapInventory(compObject, 0),
          ifmv: getIFMVUtil(compObject, 0) || 0,
         taxes: compObject?.Taxes?.[0] || null,
          salePrice: compObject?.[compPriceField]?.[0] || null,
          saleDate: compObject?.SaleDate?.[0] || null,
          TransactionID: compObject?.TransactionID?.[0] ?? null,
          adjustedPrice: compObject?.[compAdjPriceField]?.[0] || null,
          supportsReduction: false,
          ALFlag: compObject?.ALFlag?.[0] ?? ''
        };

        const result = [subject];

        mapComps.forEach((comp, index) => {
          if (comp && comp.parcel_id) {
            const { latitude, longitude } = parseCoordinates(comp.Latitude, comp.Longitude);
            
            // Include entries even without valid coordinates for address click functionality
            result.push({
              parcel_id: comp.parcel_id,
              id_number: index + 1,
              latitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? latitude : null,
              longitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? longitude : null,
              address: comp.Address || comp.PropertyInfo?.Address,
              town: comp.Town || null,
              zip: comp.ZipCode || null,
              MLSNumber: comp.MLSNumber || null,
              MLSPhotoFolder: comp.MLSPhotoFolder || null,
              SalesWebScreenshot: comp.SalesWebScreenshot || null,
              Remarks: comp.Remarks || null,
              SDCode: comp.SDCode || null,
              SDName: comp.SDName || null,
              isSubject: false,
              selected: false,
              selectedRowNumber: 0,
              ...pickMapInventory(comp),
              ifmv: getIFMVUtil(comp) || 0,
              taxes: comp.Taxes || null,
              salePrice: comp.SalePrice || null, // Always use raw SalePrice, not the field determined by run type
              saleDate: comp.SaleDate || null,
              TransactionID: comp.TransactionID ?? null,
              adjustedPrice: comp[compAdjPriceField] || null,
              supportsReduction: false,
              ALFlag: comp.ALFlag ?? ''
            });
          }
        });

        return result.map((item) => overlayMapInventory(item, persistedInventoryUpdatesRef.current[String(item.parcel_id)]));
      }

      // Use optimizedObject if in optimized view, otherwise use compObject
      const sourceObject = view === 'optimized' ? optimizedObject : compObject;
      if (!sourceObject?.parcel_id?.length) return [];

      const selectedCompIds = activeSelectionIds;
      const selectedCompMap = {};
      selectedCompIds.forEach((id, index) => {
        selectedCompMap[normalizeCompId(id)] = index + 1;
      });

      const { latitude: subjectLat, longitude: subjectLon } = parseCoordinates(
        sourceObject.Latitude?.[0],
        sourceObject.Longitude?.[0]
      );

      const result = [{
        parcel_id: sourceObject.parcel_id[0],
        id_number: 0,
        latitude: subjectLat,
        longitude: subjectLon,
        address: sourceObject.Address?.[0],
        town: sourceObject.Town?.[0] || null,
        zip: sourceObject.ZipCode?.[0] || null,
        Remarks: sourceObject.Remarks?.[0] || null,
        SDCode: sourceObject.SDCode?.[0] || null,
        SDName: sourceObject.SDName?.[0] || null,
        MLSNumber: sourceObject.MLSNumber?.[0] || null,
        MLSPhotoFolder: sourceObject.MLSPhotoFolder?.[0] || null,
        SalesWebScreenshot: sourceObject.SalesWebScreenshot?.[0] || null,
        isSubject: true,
        selected: true,
        selectedRowNumber: 0,
        ...pickMapInventory(sourceObject, 0),
        ifmv: getIFMVUtil(sourceObject, 0) || 0,
        taxes: sourceObject.Taxes?.[0] || null,
        salePrice: sourceObject.SalePrice?.[0] || null,
        saleDate: sourceObject.SaleDate?.[0] || null,
        TransactionID: sourceObject.TransactionID?.[0] ?? null,
        adjustedPrice: sourceObject[compAdjPriceField]?.[0] || null,
        supportsReduction: false,
        ALFlag: sourceObject.ALFlag?.[0] ?? ''
      }];

      const parcelIdCount = sourceObject.parcel_id.length;
      for (let i = 1; i < parcelIdCount; i++) {
          const parcelId = sourceObject.parcel_id[i];
          const { latitude, longitude } = parseCoordinates(
            sourceObject.Latitude[i],
            sourceObject.Longitude[i]
          );
          
          // Include entries even without valid coordinates for address click functionality
          const selectionId = IS_SALE_RUN
            ? (sourceObject.TransactionID?.[i] ?? parcelId)
            : parcelId;
          const selectionKey = normalizeCompId(selectionId);
          const isSelected = Boolean(selectionKey && selectedCompMap[selectionKey]);
          const selectedRowNumber = isSelected ? selectedCompMap[selectionKey] : 0;
          const adjustedPrice = sourceObject[compAdjPriceField]?.[i] || null;
          const subjectIFMV = getIFMVUtil(sourceObject, 0) || 0;
          const supportsReduction = adjustedPrice && subjectIFMV && adjustedPrice < subjectIFMV;
          
          const baseRow = {
            parcel_id: parcelId,
            id_number: i,
            latitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? latitude : null,
            longitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? longitude : null,
            address: sourceObject.Address[i],
            town: sourceObject.Town?.[i] || null,
            zip: sourceObject.ZipCode?.[i] || null,
            MLSNumber: sourceObject.MLSNumber?.[i] || null,
            MLSPhotoFolder: sourceObject.MLSPhotoFolder?.[i] || null,
            SalesWebScreenshot: sourceObject.SalesWebScreenshot?.[i] || null,
            Remarks: sourceObject.Remarks?.[i] || null,
            SDCode: sourceObject.SDCode?.[i] || null,
            SDName: sourceObject.SDName?.[i] || null,
            isSubject: false,
            selected: isSelected,
            selectedRowNumber,
            ...pickMapInventory(sourceObject, i),
            ifmv: getIFMVUtil(sourceObject, i) || 0,
            taxes: sourceObject.Taxes?.[i] || null,
            salePrice: sourceObject.SalePrice?.[i] || null, // Always use raw SalePrice, not the field determined by run type
            saleDate: sourceObject.SaleDate?.[i] || null,
            TransactionID: sourceObject.TransactionID?.[i] ?? null,
            adjustedPrice,
            supportsReduction,
            ALFlag: sourceObject.ALFlag?.[i] ?? ''
          };
            if (!IS_SALE_RUN) {
              baseRow.adjustedIFMV = sourceObject.adj_IFMV?.[i] || null;
            }
            result.push(baseRow);
      }

      return result.map((item) => overlayMapInventory(item, persistedInventoryUpdatesRef.current[String(item.parcel_id)]));
    }, [
      compObject,
      compObject?.parcel_id?.length,
      compObject?.parcel_id?.join(','),
      optimizedObject,
      optimizedObject?.parcel_id?.length,
      optimizedObject?.parcel_id?.join(','),
      view,
      memoizedSelectedComps,
      savedComps,
      activeSelectionIds,
      IS_SALE_RUN,
      getReportType,
      handleFilteredRowsChange,
      compPriceField,
      compAdjPriceField,
      negotiationComps,
      townNegotiationComps,
      rowUpdate,
    ]);

  // i hate this useeeffect flow
  const handleCompChange = useCallback(() => {
    // if selectedcomps is an array, meaning the user had picked comps
    if (Array.isArray(selectedComps)) {
      setSelectedComps(null);
      setRowUpdate(prev => !prev);
    }

    // Clear negotiation comps when comp (subject) changes
    // This ensures each subject starts with a clean slate for negotiation comps
    if (negotiationComps.length > 0) {
      setNegotiationComps([]);
    }
    if (negotiationCompsTouched) {
      setNegotiationCompsTouched(false);
    }

    // Always clear filtered parcel IDs when comp changes
    // This ensures the map shows all properties for the new comp
    if (filteredParcelIds !== null) {
      logger.log('Clearing filtered parcel IDs due to comp change');
      setFilteredParcelIds(null);
    }
    
    // Reset map filtering when comp changes
    if (mapFilteredParcelIds !== null) {
      setMapFilteredParcelIds(null);
    }
  }, [selectedComps, filteredParcelIds, mapFilteredParcelIds, memoizedLatLongs, negotiationComps.length, negotiationCompsTouched]);

    const handleCaseInfoClick = useCallback((propertyToDisplay = null) => {
      // If a property is provided, use it; otherwise, find the subject property from memoizedLatLongs
      const subjectProperty = propertyToDisplay || memoizedLatLongs.find(coord => coord.isSubject);
      if (subjectProperty) {
        // Set it as the selected property for the map
        setCompObjectToDisplay(subjectProperty);
      }
    }, [memoizedLatLongs]);

    const handleSelectAllChange = useCallback((params) => { // this function doesn't really do anything it could be fully internal in headers.
      // update your internal datagrid state (this is a little hacky) to be cleared out.
      const gridApi = params.colDef.apiRef?.current

      // Clear the selection array and indeterminate state
      params.colDef.indeterminate = false
      params.colDef.selectionArray = []

      if (!gridApi) {
        setSelectedComps([])
        setRowUpdate(!memoizedRowUpdate)
        return
      }

      // Get the actions column and update it with the cleared state
      const actionsColumn = gridApi.getColumn('actions')
      if (actionsColumn) {
        actionsColumn.indeterminate = false
        actionsColumn.selectionArray = []
        gridApi.updateColumns([actionsColumn]);
      }
      
      // Clear the selected comps state
      setSelectedComps([])
      setRowUpdate(!memoizedRowUpdate) // force a state update at lower level to clear out its local state in addition to this interal grid state.

    },[memoizedRowUpdate, setRowUpdate]);

    // Helper function to properly handle zero values for numeric fields
    const handleNumericValue = (value, defaultValue = '') => {
      if (value === 0 || value === '0') {
        return 0;
      }
      return value || defaultValue;
    };

    // Use the unified createSubjectRow service for consistency
    const createSubjectRows = () => {
      // Safety check - ensure subject exists before processing
      if (!subject) {
        return null; // Return null instead of empty array
      }
        try {
          // logger.log('subject comp object', subject)
          // Use the unified createSubjectRow service for consistency with comp row creation
          const subjectRow = createSubjectRow({
            subject,
            specialFormatters,
            handleNumericValue
          });
          
          return subjectRow;
      } catch (error) {
        logger.error("Error creating subject row:", error);
        // Return a safe fallback subject row as a single object
        return {
          id: 'subject',
          parcel_id: subject?.parcel_id || 'Subject',
          PropertyInfo: {
            Address: subject?.Address || 'Subject Property',
            parcel_id: subject?.parcel_id || '',
          },
          errors: ['ErrorCreatingRow'],
          isSubject: true
        };
      }
    };
    

    // Updated this dependency so it updates if subject changes (by looking at 1st index of your object.)
    const subjectRow = useMemo(() => {
      return(
      createSubjectRows()
    )
    }, [Object.values(memoizedCompObject).map(value => value[0]).join(',')]);

    const memoizedSubject = useMemo(() => {
      if (!subjectRow) return subjectRow;
      const overlay = persistedInventoryUpdatesRef.current[String(subjectRow.parcel_id)];
      return overlay ? { ...subjectRow, ...overlay } : subjectRow;
    }, [JSON.stringify(subjectRow), rowUpdate]);

  const negotiationCompsRef = useRef(negotiationComps);
  useEffect(() => {
    negotiationCompsRef.current = negotiationComps;
  }, [negotiationComps]);

  // Add a new comp to negotiation comps (lookup, process, and return row)
  const addNegotiationComp = useCallback(
    async (parcel_id) => {
      if (!parcel_id) return null;
      // logger.log('addNegotiationComp executing for parcel_id:', parcel_id);

      // Check if comp already exists in negotiation comps
      const existingComp = negotiationCompsRef.current?.find((row) => row?.parcel_id === parcel_id);
      if (existingComp) {
        logger.log('Comp already exists in negotiation comps');
        toast.error('Comp already exists in negotiation comps');
        return null; // Don't add duplicate
      }

      try {
        // axios post to lookup_property_inventory
        const response = await axiosInstance.post('/lookup_property_inventory', { 
          parcel_id, 
          year: getNegotiationObj.TaxYear 
        });
        const rowData = await pickSale(response.data, IS_SALE_RUN);
        if (!rowData) return null;

        // Get subject values for price adjustments
        const subjectValues = {};
        if (memoizedSubject) {
          // memoizedSubject is a flat object, not an array structure
          Object.entries(memoizedSubject).forEach(([key, value]) => {
            // Skip non-numeric fields and special fields
            if (key !== 'id' && key !== 'PropertyInfo' && key !== '#' && 
                value !== null && value !== undefined && value !== '') {
              subjectValues[key] = value;
            }
          });
        }

        logger.log('addNegotiationComp - rowData:', rowData);

        // Process the comp row data for negotiation (simplified approach)
        const processedRow = { ...rowData };
        
        // Add basic processing for negotiation comps
        const parcelId = processedRow.parcel_id || processedRow.ParcelID || '';
        processedRow.id = parcelId; // Use parcel_id as ID (same as regular rows)
        processedRow.parcel_id = parcelId;
        // stupid custom handler here for the tri-way mapping peter - you - brennan.
        processedRow.PropertyAssessment = processedRow.AssessmentTown;
        
        // Calculate distance if coordinates are available
        let distance = 0;
        if (memoizedSubject?.Latitude && memoizedSubject?.Longitude && 
            processedRow.Latitude && processedRow.Longitude) {
          distance = calculateDistance(
            memoizedSubject.Latitude, 
            memoizedSubject.Longitude,
            processedRow.Latitude, 
            processedRow.Longitude
          ) || 0;
        }
        processedRow.distance = distance;
        processedRow.Distance = distance; // Also set uppercase for column display
        
        // Don't calculate adj_price here - let the generic column definition handle it
        // This ensures proper tooltip functionality and consistent calculations
        
        // Apply field mappings with special formatters using centralized function
        const mappedRow = processRowData(processedRow, specialFormatters, handleNumericValue);
        logger.log('mappedRow', mappedRow)
        
        // Generate error flags for the comp
        const errorFlags = generateErrorFlags(mappedRow, getNegotiationObj);
        Object.assign(mappedRow, errorFlags);
        
        // Generate error codes and messages to display from the error flags
        const errorCodes = generateErrorCodes(errorFlags);
        
        if (errorCodes.length > 0) {
          mappedRow.errors = errorCodes;
          // Generate error messages using the centralized function
          mappedRow.errorMessages = generateErrorMessages(errorCodes);
        }

        return mappedRow;
      } catch (error) {
        logger.error('Error in addNegotiationComp:', error);
        return null;
      }
    },
    [getNegotiationObj, memoizedSubject, pickSale]
  );

  const defaultColumnVisibility = useMemo(() => getDefaultColumnVisibility(IS_SALE_RUN), [IS_SALE_RUN]);
  // Assessment runs hide the sale columns a shared layout may include; show the View column when the subject has a view.
  const dynamicColumnVisibility = useMemo(() => ({
    ...(!IS_SALE_RUN && { ...Object.fromEntries(SALE_ONLY_COLUMNS.map((field) => [field, false])), adj_IFMV: true }),
    ...(memoizedSubject?.View && { View: true }),
  }), [IS_SALE_RUN, memoizedSubject?.View]);
  const {
    columnVisibilityModel: memoizedColumnVisibilityModel,
    onColumnVisibilityModelChange: handleColumnVisibilityChange,
    columnWidths,
    columnLayout,
  } = useColumnLayout('caseReview', COLUMN_ORDER, defaultColumnVisibility, dynamicColumnVisibility);

    // this is where a huge cost of your rendering is. Optimizing your render cell fucntions will save render time.
    // the caseobject and savedcomps cause this to recalc header and re-render grid between every comp change.
    const hasSelectedComps = Array.isArray(activeSelectionIds) && activeSelectionIds.length > 0;
    const headers = useMemo(() => createColumnDefinitions({
      handleSelectAllChange,
      handleAddressClick,
      IS_SALE_RUN,
      getTimeAdjustmentSettings,
      getEvalDate: getEvalDate || getNegotiationObj?.evalDate,
      taxYear,
      rarHistory,
      memoizedSelectedAdjustments,
      caseObject,
      getAdjustments,
      savedComps,
      memoizedCompObject,
      handleAddAdj,
      handleDeleteAdj,
      hasSelectedComps,
    }).map((header) => ({ ...header, ...columnWidths?.[header.field] })), [handleSelectAllChange, handleAddressClick, IS_SALE_RUN, getTimeAdjustmentSettings, getEvalDate, getNegotiationObj?.evalDate, taxYear, rarHistory, memoizedSelectedAdjustments, caseObject, getAdjustments, savedComps, memoizedCompObject, handleAddAdj, handleDeleteAdj, hasSelectedComps, columnWidths]);


// Result is your filtered headers.
const headerResult = useMemo(() => {
  return headers.filter((header) => {
    return memoizedColumnVisibilityModel[header.field] !== false;
  });
}, [headers, memoizedColumnVisibilityModel]);
// Do not filter here, creating a seperate filtered headers var
  const calculateFlexForHeaders = useCallback(() => {
    // did this the quick way for your demo, this will break on big enoguh screens (it will look dumb extended all the way out)
    // Use full screen window width, not from the ref:
    // 48 px of padding on left and right, and 2px of border = subtract 50 px.
    const containerWidth = document.documentElement.clientWidth - 50

    return calculateHeaders(headerResult, memoizedColumnVisibilityModel, containerWidth);
  }, [memoizedColumnVisibilityModel, headerResult]);

  const filteredHeaders = useMemo(() => calculateFlexForHeaders(), [calculateFlexForHeaders]);

    // You changed this to use the getNegotiation Obj, but it may be able to use the taxYear now that you fixed court selection bug.
    const handleSaveComps = useCallback(async (selectedRowIds, saveOpts = {}) => {
      setIsSaving(true);
      try {
        const townNegotiationCompsSave = saveOpts.townNegotiationCompsSave === true ? 1 : 0;
        logger.log('selectedRowIds', selectedRowIds);
        logger.log('Comps to save:', selectedRowIds, 'townNegotiationCompsSave', townNegotiationCompsSave);

        const saveData = createSaveObject({
          selectedComps: selectedRowIds,
          caseIndex: comp,
          subjectRow
        });

        const { compsObject, selectedIndexRegular, selectedIndexOptimized, address, repId } = saveData;

        const latestNegotiationObj = usePersistedDataStore.getState().negotiationObj;
        const caseItem = latestNegotiationObj.cases[comp];
        const resolvedCaseNotes = resolveCaseNotesForCase(comp, undefined, caseItem);

        await batchSaveComps({
          negotiationObj: latestNegotiationObj,
          cases: [caseItem],
          user,
          options: {
            manualReviewFlag: 1,
            manualOverride: 1,
            manualCompSave: true,
            townNegotiationCompsSave,
            saveData: {
              0: {
                compsObject,
                selectedIndexRegular,
                selectedIndexOptimized,
                address,
                repId,
                caseNotes: resolvedCaseNotes,
              }
            }
          }
        });

        const latestNegotiationObjAfterSave = usePersistedDataStore.getState().negotiationObj;

        const updatedNegotiationObj = {
          ...latestNegotiationObjAfterSave,
          cases: latestNegotiationObjAfterSave.cases.map((caseItem, index) => {
            if (index === comp) {
              return {
                ...caseItem,
                Comps: compsObject,
                RunDate: new Date().toUTCString(),
                ManualReview: 1,
                ReviewedBy: user?.userId,
                SelectedIndexRegular: selectedIndexRegular,
                SelectedIndexOptimized: selectedIndexOptimized,
                CaseNotes: resolvedCaseNotes,
              };
            }
            return caseItem;
          })
        };

        setNegotiationObj(updatedNegotiationObj);
      } finally {
        setIsSaving(false);
      }
    }, [subjectRow, rowUpdate, savedComps, comp, regularComps, optimizedComps, user]);


    // State to toggle between filtered and unfiltered
    const [isFiltered, setIsFiltered] = useState(false); // default to filtered for all run types (except negotiation view) - TEMPORARILY DISABLED

    const settingsUpdated = useLocalDataStore((state) => state.settingsUpdated);
    const userFilterSettings = useMemo(() => {
      try {
        const settings = JSON.parse(localStorage.getItem('advancedSettings')) || {};
        const freshSettings = {
          ...settings,
          dateRange:
            getNegotiationObj?.earliestCutoffDate && getNegotiationObj?.latestCutoffDate
              ? [getNegotiationObj.earliestCutoffDate, getNegotiationObj.latestCutoffDate]
              : settings.dateRange,
          evalDate: getNegotiationObj?.evalDate || settings.evalDate,
        };
        return freshSettings;
      } catch (error) {
        logger.error('Error parsing advancedSettings:', error);
        return {};
      }
    }, [getNegotiationObj]);

    // Create original rows (unfiltered)
    const originalRows = useMemo(() => {
      // Use the correct comps based on the current view
      const currentComps = view === 'optimized' ? optimizedComps : 
                          view === 'town' ? negotiationComps :
                          regularComps;

      const rows = createRowsService({
        comps: currentComps, 
        view, 
        savedComps, 
        selectedComps,
        getNegotiationObj, 
        village, 
        subject,
        IS_SALE_RUN,
        compAdjPriceField,
        specialFormatters,
        handleNumericValue,
        getAdjustments,
        userFilterSettings
      });
      return rows.map((row) => {
        const overlay = persistedInventoryUpdatesRef.current[String(row.parcel_id)] || persistedInventoryUpdatesRef.current[String(row.id)];
        return overlay ? { ...row, ...overlay } : row;
      });
    }, [view, optimizedComps, regularComps, negotiationComps, savedComps, getNegotiationObj, village, subject, rowUpdate, getAdjustments, userFilterSettings]);
    // Apply user filter to create filtered rows
    const filteredRows = useMemo(() => {
      const currentSelectedComps = selectedComps == null ? savedComps : selectedComps;
      
      const filterResult = applyFiltersToRows(
        originalRows, 
        currentSelectedComps, 
        userFilterSettings,
        savedComps,
        memoizedSubject
      );
      
      // Note: Map filtering is now handled by DataGrid's onFilteredRowsChange
      // This useMemo only handles user's advanced filters (date range, square footage, etc.)
      return filterResult.filteredRows;
    }, [originalRows, userFilterSettings, selectedComps, savedComps, applyFiltersToRows, memoizedSubject]);


    // Memoize the final rows selection to prevent recalculation on toggle
    const finalRows = useMemo(() => {
      // Don't apply filtering for town view - always use originalRows
      const result = (isFiltered && view !== 'town') ? filteredRows : originalRows;
      return result;
    }, [isFiltered, filteredRows, originalRows, view]);

    // Presentation mode controls component
    const PresentationModeControls = () => {
      if (!presentationMode) return null;
      
      return (
        <div className="absolute top-1 right-4 z-[1000] flex flex-col gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg border">
          <div className="text-center text-xs font-bold text-primary-600">
            Presentation Menu
          </div>
          <div className="flex gap-2">
            <Tooltip title="Toggle Map">
              <IconButton
                size="small"
                onClick={() => setShowMap(!showMap)}
                sx={{
                  color: showMap ? 'primary.main' : 'text.disabled',
                  backgroundColor: showMap ? 'primary.light' : 'transparent',
                  '&:hover': {
                    backgroundColor: showMap ? 'primary.main' : 'action.hover',
                    color: 'white'
                  }
                }}
              >
                {showMap ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Toggle Table">
              <IconButton
                size="small"
                onClick={() => setShowTable(!showTable)}
                sx={{
                  color: showTable ? 'primary.main' : 'text.disabled',
                  backgroundColor: showTable ? 'primary.light' : 'transparent',
                  '&:hover': {
                    backgroundColor: showTable ? 'primary.main' : 'action.hover',
                    color: 'white'
                  }
                }}
              >
                <TableChartIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Exit Presentation Mode">
              <IconButton
                size="small"
                onClick={() => setPresentationMode(false)}
                sx={{
                  color: 'error.main',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'white'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      );
    };

        /* Menu button with presentation mode - commented out
    const MenuButton = () => {
      const originalCount = originalRows.length;
      const filteredCount = filteredRows.length;
      const filteredOutCount = originalCount - filteredCount;
      const hasActiveFilters = filteredOutCount > 0;
      const [menuPosition, setMenuPosition] = useState(null);
      
      const getCurrentFilterName = () => {
        try {
          const storedSettings = localStorage.getItem('advancedSettings');
          if (storedSettings) {
            const parsed = JSON.parse(storedSettings);
            return parsed.currentFilter || 'No Filter';
          }
        } catch (error) {
          logger.error('Error reading currentFilter from localStorage:', error);
        }
        return 'No Filter';
      };

      const handleMenuClick = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + window.scrollY,
          left: rect.right - 200, // 200px is menu width
        });
      };

      const handleMenuClose = () => {
        setMenuPosition(null);
      };

      const handlePresentationToggle = () => {
        setPresentationMode(!presentationMode);
        handleMenuClose();
      };

      const handleFilterToggle = () => {
        setIsFiltered(!isFiltered);
        handleMenuClose();
      };
      
      return (
        <>
          <div className="absolute top-2 right-2 z-[100]">
            <Tooltip title={
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {isFiltered ? 'Filters Active' : 'Filters OFF'}
                </div>
                {isFiltered && (
                  <div style={{ fontSize: '0.75rem' }}>
                    {getCurrentFilterName()} ({filteredCount}/{originalCount})
                  </div>
                )}
              </div>
            }>
              <Button
                variant={isFiltered ? "contained" : "outlined"}
                color={isFiltered && hasActiveFilters ? "primary" : "default"}
                size="small"
                onClick={handleMenuClick}
                startIcon={<MenuIcon />}
                sx={{ 
                  minWidth: '80px',
                  fontWeight: 'bold',
                  fontSize: '0.75rem'
                }}
              >
                Menu
              </Button>
            </Tooltip>
          </div>
          
          {menuPosition && (
            <div
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                zIndex: 9999,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                minWidth: '200px',
                padding: '8px 0',
              }}
            >
              <div
                onClick={handlePresentationToggle}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  ':hover': { backgroundColor: '#f5f5f5' }
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <PresentToAllIcon fontSize="small" />
                <span>{presentationMode ? 'Exit Presentation Mode' : 'Presentation Mode'}</span>
              </div>
              
              <div
                onClick={handleFilterToggle}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  ':hover': { backgroundColor: '#f5f5f5' }
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <FilterListIcon fontSize="small" color={isFiltered ? "primary" : "disabled"} />
                <div>
                  <div style={{ fontWeight: isFiltered ? 'bold' : 'normal' }}>
                    {isFiltered ? 'Filters Active' : 'Filters OFF'}
                  </div>
                  {isFiltered && (
                    <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {getCurrentFilterName()} ({filteredCount}/{originalCount})
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {menuPosition && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998,
              }}
              onClick={handleMenuClose}
            />
          )}
        </>
      );
    };
    */

    //   LOOK INTO NEW MAPPING LIBRARY HERE - see if you ccan also impose a side streetview partition in the
    return (
      <TimeAdjContextProvider>
        {inventorySaleDialog}
        <UpdatePollingComponent 
          isCompUpdated={isCompUpdated}
          updatedArray={updatedArray}
          compStreamObject={compStreamObject}
          savedCallbackRef={savedCallbackRef}
        />
      <div className='h-[calc(100vh-64px)]'>
        {displayModal && 
        <MiscAdjustmentModal
        toggleDisplayModal={toggleDisplayModal}
        selectedComps={memoizedSelectedComps || savedComps}
        handleAddAdjustment={handleAddAdjustment}
        >
        </MiscAdjustmentModal>
        }

        <div ref={splitContainerRef} className='h-full flex flex-col pt-2'>
          {/* Presentation Mode Controls */}
          <PresentationModeControls />
          
          {/* Map Section */}
          {showMap && isMapCollapsed ? (
            <button
              type="button"
              onClick={handleExpandMap}
              className="flex-shrink-0 h-9 w-full flex items-center justify-center gap-1 px-2 bg-gray-200 border-y border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-300 cursor-pointer"
              aria-label="Click to expand map"
            >
              Click to Expand Map
              <ExpandLessIcon fontSize="small" />
            </button>
          ) : showMap && !isMapCollapsed ? (
            <>
              <div
                ref={mapWrapperRef}
                className={presentationMode && !showTable ? 'h-full overflow-hidden' : 'min-h-0 flex-shrink-0 overflow-hidden'}
                style={presentationMode && !showTable ? undefined : { height: `${mapHeightPercent}%` }}
              >
                <div className='h-full relative overflow-hidden'>
                  {/* <MenuButton /> */}
                  <PropertyMap
                    key={`map-${presentationMode}-${showMap}-${showTable}`}
                    latLongs={memoizedLatLongs}
                    caseObject={caseObject}
                    handleCompViewClose={handleCompViewClose}
                    handleProcessDBUpdate={handleProcessDBUpdate}
                    maxCompsToShow={100}
                    compObject={memoizedCompObject}
                    isCourtView={view === 'optimized'}
                    optimizedObject={optimizedComps}
                    externalSelectedProperty={compObjectToDisplay}
                    updateSelectedComps={updateSelectedCompsFromMap}
                    selectedComps={selectedCompsForMap}
                    filteredParcelIds={filteredParcelIds}
                    mapFilteredParcelIds={mapFilteredParcelIds}
                    setMapFilteredParcelIds={setMapFilteredParcelIds}
                    onMapFilterChange={handleMapFilterChange}
                    updatedArray={updatedArray[comp]}
                    negotiationData={memoizedNegotiationMapData}
                    isNegotiationView={view === 'town'}
                    subjectData={compObject}
                    isSaleRun={IS_SALE_RUN}
                  />
                </div>
              </div>
              {showTable && !presentationMode && (
                <div
                  role="separator"
                  aria-label="Resize map"
                  onMouseDown={handleResizeStart}
                  className="flex-shrink-0 h-1.5 bg-gray-300 hover:bg-blue-400 cursor-ns-resize rounded-full my-0.5 relative z-10"
                />
              )}
            </>
          ) : null}

          {/* Table Section */}
          {showTable && (
            <div className={presentationMode && !showMap ? 'h-full' : 'min-h-0 flex flex-col flex-1'}>
              <div className="flex-1 min-h-0 overflow-hidden">
              <MuiTable
                  key={`muitable-${presentationMode}-${showMap}-${showTable}`}
                  reviewDetails={reviewDetails}
                  onCaseInfoClick={handleCaseInfoClick}
                  handleUpdateStateCallback={handleUpdateStateCallback}
                  subject={memoizedSubject}
                  subjectIFMV={subjectIFMV}
                  viewCallback={viewCallback}
                  rowUpdate={rowUpdate}
                  addCompNotInResponseCallback={addCompNotInResponseCallback}
                  handleSetPropertyInfo={handleSetPropertyInfo}
                  compStreamObject={compStreamObject}
                  selectedComps={selectedComps == null ? savedComps : selectedComps}
                  handleSaveComps={handleSaveComps}
                  originalRows={finalRows} // these are the final rows (filtered or unfiltered based on isFiltered state)
                  rawComps={view === 'optimized' ? optimizedComps : 
                           view === 'town' ? townNegotiationComps :
                           regularComps}
                  view={view}
                  savedCompPids={savedComps}
                  isSaleRun={IS_SALE_RUN}
                  headers={headers}
                  toggleDisplayModal={toggleDisplayModal}
                  tableProps={tableProps}
                  adjustmentRef={adjustmentRef}
                  updateSelectedComps={updateSelectedComps}
                  caseNotesChanged={caseNotesChanged || offerValueChanged}
                  handleProcessDBUpdate={handleProcessDBUpdate}
                  unsavedChangesRef={unsavedChangesRef}
                  isSaving={isSaving}
                  setIsSaving={handleSetSaving}
                  // Negotiation-specific props
                  updateNegotiationComps={updateNegotiationComps}
                  addNegotiationComp={addNegotiationComp}
                  handleDeleteProperty={handleDeleteProperty}
                  negotiationCompsTouched={negotiationCompsTouched}
                  markNegotiationCompsTouched={markNegotiationCompsTouched}
                  handleAddressClick={handleAddressClick}
                  handleCompChange={handleCompChange}
                  memoizedColumnVisibilityModel={memoizedColumnVisibilityModel}
                  handleColumnVisibilityChange={handleColumnVisibilityChange}
                  columnLayout={columnLayout}
                  filteredHeaders={filteredHeaders}
                  compErrors={compErrors}
                  setCompErrors={setCompErrors}
                  // Props needed for adj_price tooltips
                  memoizedSelectedAdjustments={memoizedSelectedAdjustments}
                  caseObject={caseObject}
                  caseId={caseId}
                  getAdjustments={getAdjustments}
                  savedComps={savedComps}
                  memoizedCompObject={memoizedCompObject}
                  caseNotes={caseObject.CaseNotes}
                  onFilteredRowsChange={handleFilteredRowsChange}
                  mapFilteredParcelIds={mapFilteredParcelIds}
                  isFiltered={isFiltered}
                  onCaseNotesChanged={handleCaseNotesChange}
                  onOfferValueChanged={handleOfferValueChange}
                  regularComps={regularComps}
                  comps={comps}
                  onManualRefresh={handleManualRefresh}
                  showRefreshButton={showRefreshButton}
                  subjectParcelId={subjectParcelId}
                  isRefreshing={!isCompUpdated}
                  hideCustomToolbar={presentationMode}
                  isSettled={isSettled}
                  townCompParcelIds={townCompParcelIds}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      </TimeAdjContextProvider>
    );
  };

  if (process.env.NODE_ENV === 'development') {
    CaseReview.whyDidYouRender = true;
  }

  export default CaseReview;